import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import makeStyles from '@mui/styles/makeStyles';
import classnames from 'classnames';
import { TimeSignal } from 'graphql/types';
import { NotesListSkeleton } from 'modules/loading/NotesListSkeleton';
import { useMeContext } from 'modules/me';
import { useTrackRenderCounts } from 'modules/common/components/Render/hooks';
import { useScrollbarStyles } from 'modules/common/styles/useScrollbarStyles';
import { CopyrightFooter } from 'modules/common/components/CopyrightFooter';
import { useTimeSignals } from '../hooks/useTimeSignals';
import { NoteFoldersEmptyPage } from '../NoteFoldersEmptyPage';
import NotesList from './NotesList';
import { useGroupedNotesExpansion, useNotesGroupedByDay } from './hooks';
import { useNotesSelectionForm } from './hooks/useNotesSelectionForm';
import { NotesListHeader } from './NotesListHeader';
import { NotesLoadMore } from './NotesLoadMore';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
    marginTop: theme.spacing(0.25),
    overflowY: 'auto',
    overflowX: 'hidden',
    justifyContent: 'space-between'
  },
  emptyContainer: {
    height: '95%'
  }
}));

const useHasVerticalScroll = (notes: TimeSignal[], loading: boolean) => {
  const containerRef = useRef<HTMLInputElement>(null);
  const size = notes?.length;
  const [hasVerticalScroll, setHasVerticalScroll] = useState(
    loading || size > 15
  );
  useEffect(() => {
    if (containerRef.current) {
      // this do css forced reflow so evaluate only if notes greater than 15
      setHasVerticalScroll((previousVal) => {
        if (loading) return previousVal;
        if (size > 15) return true;
        return (
          containerRef.current!.scrollHeight >
          containerRef.current!.offsetHeight
        );
      });
    }
  }, [loading, size]);
  return { hasVerticalScroll, containerRef };
};

type NotesListPageContentProp = {
  isDeleteAllTrashClicked: boolean;
};

export const NotesListPageContent: React.FC<NotesListPageContentProp> = ({
  isDeleteAllTrashClicked
}: NotesListPageContentProp) => {
  const { data, loading: loadingNotes, loadMore } = useTimeSignals();
  const notes = useMemo(
    () => (data?.timeSignals?.items as TimeSignal[]) ?? [],
    [data?.timeSignals?.items]
  );

  const classes = useStyles();

  const notesMap = useNotesGroupedByDay({ notes });
  const { id } = useMeContext() ?? {};
  const expandCollapseAllId = `${id}_mywork_expandAll`;
  const {
    onExpandCollapse,
    groupedNotesExpansionState,
    expandAll,
    onExpandCollapseAll
  } = useGroupedNotesExpansion();

  const { containerRef, hasVerticalScroll } = useHasVerticalScroll(
    notes,
    loadingNotes
  );

  useNotesSelectionForm(notes);
  useTrackRenderCounts('notes-list-page-content');

  const classesScrollbar = useScrollbarStyles();
  const [moreDataloading, setmoreDataloading] = useState(false);
  const loadMoreData = useCallback(
    async (cursor) => {
      setmoreDataloading(true);
      await loadMore(cursor);

      setmoreDataloading(false);
    },
    [loadMore]
  );
  const moreLoading = useMemo(() => {
    if (data?.timeSignals?.items.length == 0 && moreDataloading) {
      return true;
    }
    return false;
  }, [data?.timeSignals?.items, moreDataloading]);

  if ((loadingNotes || moreLoading) && !isDeleteAllTrashClicked) {
    return <NotesListSkeleton showHeaders={false} />;
  }

  return (
    <>
      {Boolean(notes?.length) && !isDeleteAllTrashClicked && (
        <NotesListHeader
          notes={notes}
          totalNotesCount={data?.timeSignals.totalCount ?? notes.length}
          onExpandCollapseAll={onExpandCollapseAll}
          expandCollapseAllId={expandCollapseAllId}
          hasVerticalScroll={hasVerticalScroll}
        />
      )}
      <div
        ref={containerRef}
        className={classnames(classes.root, classesScrollbar.scrollbar, {
          [classes.emptyContainer]: !notes.length
        })}
      >
        {!isDeleteAllTrashClicked && notes?.length ? (
          <NotesList
            notesMap={notesMap}
            onExpandCollapse={onExpandCollapse}
            expandAll={expandAll}
            groupedNotesExpansionState={groupedNotesExpansionState}
          />
        ) : (
          <NoteFoldersEmptyPage />
        )}
        {data && !isDeleteAllTrashClicked && (
          <NotesLoadMore
            loading={loadingNotes}
            cursor={data.timeSignals.cursor}
            loadMore={loadMoreData}
          />
        )}
        {(!data?.timeSignals.cursor || isDeleteAllTrashClicked) && (
          <CopyrightFooter />
        )}
      </div>
    </>
  );
};
export default React.memo(NotesListPageContent);
